import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Package, Users, ShoppingBag, BarChart3, Plus, Pencil, Trash2, 
  Search, X, Save, Shield, User, Mail, Calendar, TrendingUp,
  AlertTriangle, CheckCircle, Truck, Clock, MessageCircle, Send, ArrowLeft, Tag, Percent, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const RARITIES_BY_GAME = {
  yugioh: [
    { value: 'common', label: 'Common' },
    { value: 'rare', label: 'Rare' },
    { value: 'super_rare', label: 'Super Rare' },
    { value: 'ultra_rare', label: 'Ultra Rare' },
    { value: 'secret_rare', label: 'Secret Rare' },
    { value: 'starlight_rare', label: 'Starlight Rare' },
    { value: 'ghost_rare', label: 'Ghost Rare' },
    { value: 'qcsr', label: 'Quarter Century Secret Rare' },
  ],
  magic: [
    { value: 'common', label: 'Common' },
    { value: 'uncommon', label: 'Uncommon' },
    { value: 'rare', label: 'Rare' },
    { value: 'mythic_rare', label: 'Mythic Rare' },
    { value: 'special', label: 'Special / Bonus' },
  ],
  pokemon: [
    { value: 'common', label: 'Common' },
    { value: 'uncommon', label: 'Uncommon' },
    { value: 'rare', label: 'Rare' },
    { value: 'holo_rare', label: 'Holo Rare' },
    { value: 'ultra_rare', label: 'Ultra Rare' },
    { value: 'full_art', label: 'Full Art' },
    { value: 'secret_rare', label: 'Secret Rare' },
    { value: 'illustration_rare', label: 'Illustration Rare' },
    { value: 'special_art_rare', label: 'Special Art Rare' },
    { value: 'hyper_rare', label: 'Hyper Rare' },
  ],
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== DASHBOARD TAB ====================
const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-2xl font-bold text-white">{stats.users.total}</span>
          </div>
          <p className="text-gray-400">Utilisateurs</p>
          <p className="text-sm text-gray-500">{stats.users.admins} admin(s)</p>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-fuchsia-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-fuchsia-400" />
            </div>
            <span className="text-2xl font-bold text-white">{stats.products.total}</span>
          </div>
          <p className="text-gray-400">Produits</p>
          <p className="text-sm text-yellow-400">{stats.products.low_stock} stock faible</p>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-green-400" />
            </div>
            <span className="text-2xl font-bold text-white">{stats.orders.total}</span>
          </div>
          <p className="text-gray-400">Commandes</p>
          <p className="text-sm text-orange-400">{stats.orders.pending} en attente</p>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-400" />
            </div>
            <span className="text-2xl font-bold text-white">{stats.revenue.total} €</span>
          </div>
          <p className="text-gray-400">Revenus</p>
          <p className="text-sm text-green-400">{stats.orders.delivered} livrées</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-fuchsia-400" />
            Commandes récentes
          </h3>
          {stats.orders.recent.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucune commande</p>
          ) : (
            <div className="space-y-3">
              {stats.orders.recent.map((order) => (
                <div key={order.order_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium">{order.user_name || order.user_email}</p>
                    <p className="text-gray-500 text-xs">{order.items?.length || 0} article(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-fuchsia-400 font-semibold">{order.total?.toFixed(2)} €</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                      order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Users */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Nouveaux utilisateurs
          </h3>
          {stats.users.recent.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucun utilisateur</p>
          ) : (
            <div className="space-y-3">
              {stats.users.recent.map((user) => (
                <div key={user.user_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-fuchsia-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">{user.name}</p>
                      <p className="text-gray-500 text-xs">{user.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    user.role === 'admin' ? 'bg-fuchsia-500/20 text-fuchsia-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {user.role || 'user'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== USERS TAB ====================
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success(`Rôle mis à jour`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteDialog.user.user_id}`, { withCredentials: true });
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setDeleteDialog({ open: false, user: null });
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Rechercher un utilisateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Utilisateur</th>
                <th className="text-left p-4 text-gray-400 font-medium hidden md:table-cell">Email</th>
                <th className="text-left p-4 text-gray-400 font-medium">Rôle</th>
                <th className="text-left p-4 text-gray-400 font-medium hidden sm:table-cell">Inscrit le</th>
                <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-fuchsia-400" />
                        </div>
                      )}
                      <span className="text-white font-medium">{user.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-400 hidden md:table-cell">{user.email}</td>
                  <td className="p-4">
                    <Select value={user.role || 'user'} onValueChange={(v) => handleRoleChange(user.user_id, v)}>
                      <SelectTrigger className="w-28 bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        <SelectItem value="user" className="text-white">User</SelectItem>
                        <SelectItem value="admin" className="text-white">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-gray-400 hidden sm:table-cell">{formatDate(user.created_at)}</td>
                  <td className="p-4 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteDialog({ open: true, user })}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: deleteDialog.user })}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              L'utilisateur "{deleteDialog.user?.name}" sera supprimé définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-white hover:bg-white/10">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== ORDERS TAB ====================
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`, { withCredentials: true });
      setOrders(response.data.orders);
    } catch (error) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'shipped': return <Truck className="w-4 h-4 text-blue-400" />;
      case 'cancelled': return <X className="w-4 h-4 text-red-400" />;
      default: return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Filter */}
      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10">
            <SelectItem value="all" className="text-white">Toutes les commandes</SelectItem>
            <SelectItem value="pending" className="text-white">En attente</SelectItem>
            <SelectItem value="confirmed" className="text-white">Confirmées</SelectItem>
            <SelectItem value="processing" className="text-white">En traitement</SelectItem>
            <SelectItem value="shipped" className="text-white">Expédiées</SelectItem>
            <SelectItem value="delivered" className="text-white">Livrées</SelectItem>
            <SelectItem value="cancelled" className="text-white">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders */}
      {orders.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Aucune commande</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.order_id} className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(order.status)}
                    <span className="text-white font-semibold">Commande #{order.order_id?.slice(-8)}</span>
                  </div>
                  <p className="text-gray-400 text-sm">{order.user_name} ({order.user_email})</p>
                  <p className="text-gray-500 text-xs">{formatDate(order.created_at)}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-fuchsia-400">{order.total?.toFixed(2)} €</span>
                  <Select value={order.status} onValueChange={(v) => handleStatusChange(order.order_id, v)}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="pending" className="text-white">En attente</SelectItem>
                      <SelectItem value="confirmed" className="text-white">Confirmée</SelectItem>
                      <SelectItem value="processing" className="text-white">En traitement</SelectItem>
                      <SelectItem value="shipped" className="text-white">Expédiée</SelectItem>
                      <SelectItem value="delivered" className="text-white">Livrée</SelectItem>
                      <SelectItem value="cancelled" className="text-white">Annulée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border-t border-white/10 pt-4">
                <p className="text-gray-400 text-sm mb-2">Articles ({order.items?.length || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {order.items?.map((item, idx) => (
                    <span key={idx} className="text-xs bg-white/5 text-gray-300 px-2 py-1 rounded">
                      {item.quantity}× {item.name}
                    </span>
                  ))}
                </div>
                
                {/* Shipping Address */}
                {order.shipping_address && (
                  <div className="mt-4 pt-3 border-t border-white/5" data-testid={`admin-order-address-${order.order_id}`}>
                    <p className="text-gray-400 text-xs mb-1 font-medium">Livraison :</p>
                    <p className="text-gray-300 text-xs">
                      {order.shipping_address.first_name} {order.shipping_address.last_name} — {order.shipping_address.address}
                      {order.shipping_address.address2 ? `, ${order.shipping_address.address2}` : ''}, {order.shipping_address.postal_code} {order.shipping_address.city}
                      {order.shipping_address.phone ? ` — Tél: ${order.shipping_address.phone}` : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== PRODUCTS TAB ====================
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGame, setFilterGame] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', image_url: '',
    game: 'yugioh', category: 'none', stock: '10', rarity: '', weight: '0'
  });
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data.products);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGame = filterGame === 'all' || p.game === filterGame;
    const matchesCategory = filterCategory === 'all' || (p.category || '') === filterCategory || (filterCategory === 'none' && !p.category);
    return matchesSearch && matchesGame && matchesCategory;
  }).sort((a, b) => {
    const gameOrder = { yugioh: 0, magic: 1, pokemon: 2, accessory: 3 };
    const catOrder = { single: 0, booster: 1, deck: 2, '': 3 };
    const gA = gameOrder[a.game] ?? 4;
    const gB = gameOrder[b.game] ?? 4;
    if (gA !== gB) return gA - gB;
    const cA = catOrder[a.category || ''] ?? 4;
    const cB = catOrder[b.category || ''] ?? 4;
    if (cA !== cB) return cA - cB;
    return a.name.localeCompare(b.name);
  });

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name, description: product.description, price: product.price.toString(),
        image_url: product.image_url, game: product.game, category: product.category || 'none',
        stock: product.stock.toString(), rarity: product.rarity || '', weight: (product.weight || 0).toString()
      });
    } else {
      setEditingProduct(null);
      setFormData({ name: '', description: '', price: '', image_url: '', game: 'yugioh', category: 'none', stock: '10', rarity: '', weight: '0' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price || !formData.image_url) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      const data = { ...formData, price: parseFloat(formData.price), stock: parseInt(formData.stock) || 0, weight: parseFloat(String(formData.weight).replace(',', '.')) || 0 };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.product_id}`, data, { withCredentials: true });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/admin/products`, data, { withCredentials: true });
        toast.success('Produit créé');
      }
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.product) return;
    try {
      await axios.delete(`${API}/admin/products/${deleteDialog.product.product_id}`, { withCredentials: true });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setDeleteDialog({ open: false, product: null });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white w-64"
            />
          </div>
          <Select value={filterGame} onValueChange={setFilterGame}>
            <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10">
              <SelectItem value="all" className="text-white">Tous les jeux</SelectItem>
              <SelectItem value="yugioh" className="text-white">Yu-Gi-Oh!</SelectItem>
              <SelectItem value="magic" className="text-white">Magic</SelectItem>
              <SelectItem value="pokemon" className="text-white">Pokémon</SelectItem>
              <SelectItem value="accessory" className="text-white">Accessoire</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10">
              <SelectItem value="all" className="text-white">Toutes catégories</SelectItem>
              <SelectItem value="single" className="text-white">Carte</SelectItem>
              <SelectItem value="booster" className="text-white">Booster</SelectItem>
              <SelectItem value="deck" className="text-white">Deck</SelectItem>
              <SelectItem value="none" className="text-white">Aucune</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-fuchsia-600 hover:bg-fuchsia-500">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {/* Products Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Produit</th>
                <th className="text-left p-4 text-gray-400 font-medium hidden md:table-cell">Jeu</th>
                <th className="text-left p-4 text-gray-400 font-medium hidden md:table-cell">Catégorie</th>
                <th className="text-left p-4 text-gray-400 font-medium">Prix</th>
                <th className="text-left p-4 text-gray-400 font-medium hidden sm:table-cell">Stock</th>
                <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.product_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={product.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-white/5" />
                      <span className="text-white font-medium truncate max-w-[200px]">{product.name}</span>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`px-2 py-1 rounded text-xs ${
                      product.game === 'yugioh' ? 'badge-yugioh' :
                      product.game === 'magic' ? 'badge-magic' :
                      product.game === 'accessory' ? 'bg-gray-700 text-gray-300' : 'badge-pokemon'
                    }`}>
                      {product.game === 'yugioh' ? 'Yu-Gi-Oh!' : product.game === 'magic' ? 'Magic' : product.game === 'accessory' ? 'Accessoire' : 'Pokémon'}
                    </span>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="text-gray-400 text-sm">
                      {product.category === 'single' ? 'Carte' : product.category === 'booster' ? 'Booster' : product.category === 'deck' ? 'Deck' : '—'}
                    </span>
                  </td>
                  <td className="p-4 text-fuchsia-400 font-semibold">{product.price.toFixed(2)} €</td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className={product.stock > 5 ? 'text-green-400' : product.stock > 0 ? 'text-yellow-400' : 'text-red-400'}>
                      {product.stock}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <Button size="icon" variant="ghost" onClick={() => handleOpenModal(product)} className="text-gray-400 hover:text-white">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteDialog({ open: true, product })} className="text-gray-400 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifier' : 'Ajouter'} un produit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Nom *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Jeu</Label>
                <Select value={formData.game} onValueChange={(v) => setFormData({...formData, game: v})}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="yugioh" className="text-white">Yu-Gi-Oh!</SelectItem>
                    <SelectItem value="magic" className="text-white">Magic</SelectItem>
                    <SelectItem value="pokemon" className="text-white">Pokémon</SelectItem>
                    <SelectItem value="accessory" className="text-white">Accessoire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="none" className="text-white">Aucune</SelectItem>
                    <SelectItem value="single" className="text-white">Carte</SelectItem>
                    <SelectItem value="booster" className="text-white">Booster</SelectItem>
                    <SelectItem value="deck" className="text-white">Deck</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Prix (€) *</Label>
                <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Stock</Label>
                <Input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Poids (g)</Label>
                <Input type="text" inputMode="decimal" value={formData.weight} onChange={(e) => { const v = e.target.value.replace(/[^0-9.,]/g, ''); setFormData({...formData, weight: v}); }} placeholder="ex: 12,5" className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            {formData.game !== 'accessory' && (
              <div>
                <Label className="text-gray-300">Rareté</Label>
                <Select value={formData.rarity} onValueChange={(v) => setFormData({...formData, rarity: v})}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Sélectionner la rareté" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {(RARITIES_BY_GAME[formData.game] || []).map(r => (
                      <SelectItem key={r.value} value={r.value} className="text-white">{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-gray-300">URL Image *</Label>
              <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" required />
            </div>
            <div>
              <Label className="text-gray-300">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="border-white/10 text-white">Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-500">
                {saving ? 'Enregistrement...' : <><Save className="w-4 h-4 mr-2" />{editingProduct ? 'Mettre à jour' : 'Créer'}</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, product: deleteDialog.product })}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">"{deleteDialog.product?.name}" sera supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-white hover:bg-white/10">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== CHAT TAB ====================
const ChatTab = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/chat/conversations`, { withCredentials: true });
      setConversations(res.data.conversations);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const openConversation = async (convId) => {
    setSelectedConv(convId);
    try {
      const res = await axios.get(`${API}/admin/chat/messages/${convId}`, { withCredentials: true });
      setMessages(res.data.messages);
      await axios.put(`${API}/admin/chat/read/${convId}`, {}, { withCredentials: true });
      fetchConversations();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (selectedConv) {
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/chat/messages/${selectedConv}`, { withCredentials: true });
          setMessages(res.data.messages);
          await axios.put(`${API}/admin/chat/read/${selectedConv}`, {}, { withCredentials: true });
        } catch { /* ignore */ }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending || !selectedConv) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/chat/reply/${selectedConv}`, { message: reply.trim() }, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      setReply('');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    }
    setSending(false);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return formatTime(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const selectedConvInfo = conversations.find(c => c.conversation_id === selectedConv);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden" style={{ height: '600px' }} data-testid="admin-chat-tab">
      <div className="flex h-full">
        {/* Conversations List */}
        <div className={`${selectedConv ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-white/10`}>
          <div className="p-4 border-b border-white/10">
            <h3 className="text-white font-semibold text-sm">Conversations</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucune conversation</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => openConversation(conv.conversation_id)}
                  className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left border-b border-white/5 ${
                    selectedConv === conv.conversation_id ? 'bg-white/5' : ''
                  }`}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  {conv.user_picture ? (
                    <img src={conv.user_picture} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-fuchsia-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-white text-sm font-medium truncate">{conv.user_name}</p>
                      <span className="text-gray-500 text-xs flex-shrink-0">{formatDate(conv.last_at)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-gray-500 text-xs truncate">
                        {conv.last_sender_role === 'admin' ? 'Vous: ' : ''}{conv.last_message}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-fuchsia-600 rounded-full text-xs flex items-center justify-center text-white flex-shrink-0 ml-2">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages Panel */}
        <div className={`${selectedConv ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 p-4 border-b border-white/10">
                <button onClick={() => setSelectedConv(null)} className="md:hidden text-gray-400 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                {selectedConvInfo?.user_picture ? (
                  <img src={selectedConvInfo.user_picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-fuchsia-400" />
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-medium">{selectedConvInfo?.user_name}</p>
                  <p className="text-gray-500 text-xs">{selectedConvInfo?.user_email}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="admin-chat-messages">
                {messages.map((msg) => {
                  const isAdmin = msg.sender_role === 'admin';
                  return (
                    <div key={msg.message_id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                        isAdmin
                          ? 'bg-fuchsia-600 text-white rounded-br-sm'
                          : 'bg-white/10 text-gray-200 rounded-bl-sm'
                      }`} data-testid={`admin-msg-${msg.message_id}`}>
                        <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                        <p className={`text-[10px] mt-1 ${isAdmin ? 'text-white/60' : 'text-gray-500'} text-right`}>{formatTime(msg.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <form onSubmit={handleReply} className="p-3 border-t border-white/10">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Répondre..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500/50"
                    data-testid="admin-chat-reply-input"
                  />
                  <Button type="submit" disabled={!reply.trim() || sending} className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-xl px-3 disabled:opacity-40" data-testid="admin-chat-send-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Sélectionnez une conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN ADMIN PAGE ====================

// ==================== PROMOTIONS TAB ====================
const GAME_OPTIONS = [
  { value: 'yugioh', label: 'Yu-Gi-Oh!' },
  { value: 'magic', label: 'Magic' },
  { value: 'pokemon', label: 'Pokémon' },
  { value: 'accessory', label: 'Accessoire' },
];
const CATEGORY_OPTIONS = [
  { value: 'single', label: 'Cartes' },
  { value: 'booster', label: 'Boosters' },
  { value: 'deck', label: 'Decks' },
];

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', discount_percent: '10', target_product: '', target_game: '', target_category: '',
    active: true, start_date: '', end_date: ''
  });

  const fetchPromos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promotions`, { withCredentials: true });
      setPromos(res.data.promotions);
    } catch { /* */ }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async (game, category) => {
    try {
      const params = new URLSearchParams();
      if (game) params.append('game', game);
      if (category) params.append('category', category);
      const res = await axios.get(`${API}/admin/products/list?${params.toString()}`, { withCredentials: true });
      setProductsList(res.data.products);
    } catch { setProductsList([]); }
  }, []);

  useEffect(() => { fetchPromos(); fetchProducts('', ''); }, [fetchPromos, fetchProducts]);

  const handleOpenModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        name: promo.name, discount_percent: promo.discount_percent.toString(),
        target_product: promo.target_product || '', target_game: promo.target_game || '',
        target_category: promo.target_category || '',
        active: promo.active, start_date: promo.start_date || '', end_date: promo.end_date || ''
      });
      fetchProducts(promo.target_game || '', promo.target_category || '');
    } else {
      setEditingPromo(null);
      setFormData({ name: '', discount_percent: '10', target_product: '', target_game: '', target_category: '', active: true, start_date: '', end_date: '' });
      fetchProducts('', '');
    }
    setIsModalOpen(true);
  };

  const handleGameChange = (v) => {
    const game = v === 'none' ? '' : v;
    setFormData(prev => ({...prev, target_game: game, target_product: ''}));
    fetchProducts(game, formData.target_category);
  };

  const handleCategoryChange = (v) => {
    const cat = v === 'none' ? '' : v;
    setFormData(prev => ({...prev, target_category: cat, target_product: ''}));
    fetchProducts(formData.target_game, cat);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Donnez un nom à la promotion'); return; }
    if (!formData.target_product && !formData.target_game && !formData.target_category) {
      toast.error('Sélectionnez au moins une cible (produit, jeu ou catégorie)'); return;
    }
    setSaving(true);
    try {
      const data = {
        name: formData.name,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        target_product: formData.target_product || null,
        target_game: formData.target_game || null,
        target_category: formData.target_category || null,
        active: formData.active,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };
      if (editingPromo) {
        await axios.put(`${API}/admin/promotions/${editingPromo.promo_id}`, data, { withCredentials: true });
        toast.success('Promotion mise à jour');
      } else {
        await axios.post(`${API}/admin/promotions`, data, { withCredentials: true });
        toast.success('Promotion créée');
      }
      setIsModalOpen(false);
      fetchPromos();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const handleToggle = async (promo) => {
    try {
      await axios.put(`${API}/admin/promotions/${promo.promo_id}`, { active: !promo.active }, { withCredentials: true });
      fetchPromos();
      toast.success(promo.active ? 'Promo désactivée' : 'Promo activée');
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (promoId) => {
    try {
      await axios.delete(`${API}/admin/promotions/${promoId}`, { withCredentials: true });
      toast.success('Promotion supprimée');
      fetchPromos();
    } catch { toast.error('Erreur'); }
  };

  const getGameLabel = (v) => GAME_OPTIONS.find(g => g.value === v)?.label || v;
  const getCatLabel = (v) => CATEGORY_OPTIONS.find(c => c.value === v)?.label || v;
  const getProductLabel = (v) => productsList.find(p => p.value === v)?.label || v;

  const getPromoDescription = (promo) => {
    const parts = [];
    if (promo.target_game) parts.push(getGameLabel(promo.target_game));
    if (promo.target_category) parts.push(getCatLabel(promo.target_category));
    if (promo.target_product) parts.push(getProductLabel(promo.target_product));
    return parts.join(' + ') || 'Aucune cible';
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="text-white text-lg font-semibold">{promos.length} promotion{promos.length !== 1 ? 's' : ''}</h2>
        <Button onClick={() => handleOpenModal()} className="bg-fuchsia-600 hover:bg-fuchsia-500">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle promo
        </Button>
      </div>

      {promos.length === 0 ? (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-12 text-center">
          <Tag className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Aucune promotion pour le moment</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {promos.map((promo) => (
            <div key={promo.promo_id} className={`bg-[#1a1a1a] rounded-xl border p-5 flex items-center gap-4 ${promo.active ? 'border-fuchsia-500/30' : 'border-white/10 opacity-60'}`}>
              <div className="w-12 h-12 rounded-lg bg-fuchsia-500/20 flex items-center justify-center shrink-0">
                <Percent className="w-6 h-6 text-fuchsia-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-semibold truncate">{promo.name}</h3>
                  <span className="px-2 py-0.5 rounded text-xs bg-fuchsia-600/20 text-fuchsia-400 font-bold">-{promo.discount_percent}%</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${promo.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {promo.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  {getPromoDescription(promo)}
                  {promo.start_date && <span className="ml-3">Du {promo.start_date.slice(0, 10)}</span>}
                  {promo.end_date && <span> au {promo.end_date.slice(0, 10)}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => handleToggle(promo)} className="text-gray-400 hover:text-white" title={promo.active ? 'Désactiver' : 'Activer'}>
                  {promo.active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleOpenModal(promo)} className="text-gray-400 hover:text-white">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(promo.promo_id)} className="text-gray-400 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Promo Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPromo ? 'Modifier' : 'Nouvelle'} promotion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Nom *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Ex: Soldes été" className="mt-1 bg-white/5 border-white/10 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Réduction (%)</Label>
                <Input type="number" min="1" max="99" value={formData.discount_percent} onChange={(e) => setFormData({...formData, discount_percent: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            <div className="border border-white/10 rounded-lg p-4 space-y-3">
              <p className="text-gray-400 text-sm font-medium">Cibles (au moins une)</p>
              <div>
                <Label className="text-gray-300 text-sm">Jeu (optionnel)</Label>
                <Select value={formData.target_game || 'none'} onValueChange={handleGameChange}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="none" className="text-white">Tous les jeux</SelectItem>
                    {GAME_OPTIONS.map(g => <SelectItem key={g.value} value={g.value} className="text-white">{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Catégorie (optionnel)</Label>
                <Select value={formData.target_category || 'none'} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="none" className="text-white">Toutes catégories</SelectItem>
                    {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Produit spécifique (optionnel)</Label>
                <Select value={formData.target_product || 'none'} onValueChange={(v) => setFormData({...formData, target_product: v === 'none' ? '' : v})}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 max-h-60">
                    <SelectItem value="none" className="text-white">Aucun produit</SelectItem>
                    {productsList.map(p => <SelectItem key={p.value} value={p.value} className="text-white">{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Début (optionnel)</Label>
                <Input type="date" value={formData.start_date ? formData.start_date.slice(0, 10) : ''} onChange={(e) => setFormData({...formData, start_date: e.target.value ? e.target.value + 'T00:00:00Z' : ''})} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Fin (optionnel)</Label>
                <Input type="date" value={formData.end_date ? formData.end_date.slice(0, 10) : ''} onChange={(e) => setFormData({...formData, end_date: e.target.value ? e.target.value + 'T23:59:59Z' : ''})} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)} className="text-gray-400">Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-500">
                <Save className="w-4 h-4 mr-2" />
                {editingPromo ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};



const PreordersTab = () => {
  const [preorders, setPreorders] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPreorder, setEditingPreorder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', target_product: '', target_game: '', target_category: '',
    release_date: '', active: true
  });

  const fetchPreorders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/preorders`, { withCredentials: true });
      setPreorders(res.data.preorders);
    } catch { /* */ }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async (game, category) => {
    try {
      const params = new URLSearchParams();
      if (game) params.append('game', game);
      if (category) params.append('category', category);
      const res = await axios.get(`${API}/admin/products/list?${params.toString()}`, { withCredentials: true });
      setProductsList(res.data.products);
    } catch { setProductsList([]); }
  }, []);

  useEffect(() => { fetchPreorders(); fetchProducts('', ''); }, [fetchPreorders, fetchProducts]);

  const handleOpenModal = (preorder = null) => {
    if (preorder) {
      setEditingPreorder(preorder);
      setFormData({
        name: preorder.name, target_product: preorder.target_product || '',
        target_game: preorder.target_game || '', target_category: preorder.target_category || '',
        release_date: preorder.release_date || '', active: preorder.active
      });
    } else {
      setEditingPreorder(null);
      setFormData({ name: '', target_product: '', target_game: '', target_category: '', release_date: '', active: true });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) { toast.error('Donnez un nom à la précommande'); return; }
    if (!formData.target_product && !formData.target_game && !formData.target_category) {
      toast.error('Ciblez au moins un produit, jeu ou catégorie'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        target_product: formData.target_product || null,
        target_game: formData.target_game || null,
        target_category: formData.target_category || null,
        release_date: formData.release_date || null
      };
      if (editingPreorder) {
        await axios.put(`${API}/admin/preorders/${editingPreorder.preorder_id}`, payload, { withCredentials: true });
        toast.success('Précommande mise à jour');
      } else {
        await axios.post(`${API}/admin/preorders`, payload, { withCredentials: true });
        toast.success('Précommande créée');
      }
      setIsModalOpen(false);
      fetchPreorders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette précommande ?')) return;
    try {
      await axios.delete(`${API}/admin/preorders/${id}`, { withCredentials: true });
      toast.success('Précommande supprimée');
      fetchPreorders();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleToggle = async (preorder) => {
    try {
      await axios.put(`${API}/admin/preorders/${preorder.preorder_id}`, { active: !preorder.active }, { withCredentials: true });
      fetchPreorders();
    } catch { toast.error('Erreur'); }
  };

  const gameLabels = { yugioh: 'Yu-Gi-Oh!', magic: 'Magic', pokemon: 'Pokémon', accessory: 'Accessoire' };
  const catLabels = { booster: 'Booster', single: 'Single', deck: 'Deck' };

  const filteredProducts = productsList.filter(p => {
    if (formData.target_game && p.game !== formData.target_game) return false;
    if (formData.target_category && p.category !== formData.target_category) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Précommandes</h2>
        <Button onClick={() => handleOpenModal()} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="add-preorder-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle Précommande
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400">Chargement...</p>
      ) : preorders.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aucune précommande configurée</p>
      ) : (
        <div className="space-y-3">
          {preorders.map(preorder => (
            <div key={preorder.preorder_id} className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center justify-between" data-testid={`preorder-${preorder.preorder_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-white font-medium">{preorder.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${preorder.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {preorder.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-400 flex-wrap">
                  {preorder.target_game && <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">{gameLabels[preorder.target_game] || preorder.target_game}</span>}
                  {preorder.target_category && <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">{catLabels[preorder.target_category] || preorder.target_category}</span>}
                  {preorder.target_product && <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">Produit spécifique</span>}
                  {preorder.release_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Sortie: {preorder.release_date}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleToggle(preorder)} className="text-gray-400 hover:text-white">
                  {preorder.active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleOpenModal(preorder)} className="text-gray-400 hover:text-white">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(preorder.preorder_id)} className="text-gray-400 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPreorder ? 'Modifier' : 'Nouvelle'} Précommande</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Nom</Label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Ex: Préco Aetherdrift" className="mt-1 bg-white/5 border-white/10 text-white" data-testid="preorder-name-input" />
            </div>
            <div>
              <Label className="text-gray-300">Jeu</Label>
              <Select value={formData.target_game} onValueChange={v => { setFormData({...formData, target_game: v === 'all' ? '' : v, target_product: ''}); fetchProducts(v === 'all' ? '' : v, formData.target_category); }}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Tous les jeux" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  <SelectItem value="all">Tous les jeux</SelectItem>
                  <SelectItem value="yugioh">Yu-Gi-Oh!</SelectItem>
                  <SelectItem value="magic">Magic</SelectItem>
                  <SelectItem value="pokemon">Pokémon</SelectItem>
                  <SelectItem value="accessory">Accessoire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Catégorie</Label>
              <Select value={formData.target_category} onValueChange={v => { setFormData({...formData, target_category: v === 'all' ? '' : v, target_product: ''}); fetchProducts(formData.target_game, v === 'all' ? '' : v); }}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Toutes les catégories" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  <SelectItem value="booster">Booster</SelectItem>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="deck">Deck</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Produit spécifique (optionnel)</Label>
              <Select value={formData.target_product} onValueChange={v => setFormData({...formData, target_product: v === 'none' ? '' : v})}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Aucun (tous)" /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 max-h-48">
                  <SelectItem value="none">Aucun (tous les produits ciblés)</SelectItem>
                  {productsList.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Date de sortie estimée</Label>
              <Input type="date" value={formData.release_date} onChange={e => setFormData({...formData, release_date: e.target.value})} className="mt-1 bg-white/5 border-white/10 text-white" data-testid="preorder-date-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.active} onChange={e => setFormData({...formData, active: e.target.checked})} className="rounded" id="preorder-active" />
              <Label htmlFor="preorder-active" className="text-gray-300">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-white/20 text-white hover:bg-white/10">Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="preorder-save-btn">
              {saving ? 'Enregistrement...' : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export const AdminPage = () => {
  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#050505]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-fuchsia-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white font-outfit">Administration</h1>
            <p className="text-gray-400">Gérez votre boutique</p>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-[#1a1a1a] border border-white/10 p-1 flex-wrap h-auto">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <Package className="w-4 h-4 mr-2" /> Produits
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <ShoppingBag className="w-4 h-4 mr-2" /> Commandes
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <Users className="w-4 h-4 mr-2" /> Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <MessageCircle className="w-4 h-4 mr-2" /> Messages
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <Tag className="w-4 h-4 mr-2" /> Promos
            </TabsTrigger>
            <TabsTrigger value="preorders" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-gray-400">
              <Package className="w-4 h-4 mr-2" /> Précommandes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab />
          </TabsContent>
          <TabsContent value="products">
            <ProductsTab />
          </TabsContent>
          <TabsContent value="orders">
            <OrdersTab />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="chat">
            <ChatTab />
          </TabsContent>
          <TabsContent value="promos">
            <PromosTab />
          </TabsContent>
          <TabsContent value="preorders">
            <PreordersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
